/**
 * Attachments and the connection
 * (docs/plans/2026-09-29-attachments-move-with-their-map.md, stage 2 step 4):
 *
 * - once an online map's sync completes (when it opens, when the connection
 *   comes back, at start), its attachments are copied to this device in the
 *   background, so they show offline too (only into free room; files the
 *   view already downloaded are skipped, it kept their copy);
 * - files attached while the server could not be reached are sent when it
 *   can again (at start, on the browser's `online` event, and when the
 *   server indicator turns back to connected).
 */
import {
  collectAttachmentRefsFromTree,
  copyAttachmentsForOffline,
  sendWaitingAttachments,
  attachmentsOnDevice,
} from "./attachment-service.js";
import { loadWaitingFiles } from "./waiting-attachments.js";
import { on, Events } from "../core/events.js";
import { ServerLevel } from "../core/server-status.js";
import { notify } from "../host.js";
import { NOTICE } from "../notices.js";
import { t } from "../../shared/i18n/index.js";

let deps = null;
let copyingFor = null;

async function copyOpenMap() {
  const docId = deps.getDocId();
  if (!docId || !deps.getCurrentUser() || attachmentsOnDevice(docId) || copyingFor === docId) return;
  const ids = collectAttachmentRefsFromTree(deps.getTree(), deps.getDocNotes(), deps.getRoutineEvents());
  if (!ids.length) return;
  copyingFor = docId;
  try {
    await copyAttachmentsForOffline(docId, ids, {
      stillWanted: () => deps.getDocId() === docId && !!deps.getCurrentUser(),
    });
  } catch (error) {
    console.warn("[OfflineFiles] Background copy stopped:", error?.message || error);
  } finally {
    copyingFor = null;
  }
}

async function sendWaiting() {
  if (!deps.getCurrentUser()) return;
  try {
    const { sent, refused } = await sendWaitingAttachments();
    if (sent) notify({ code: NOTICE.WAITING_FILES_SENT, level: "info", message: t("vault.device.waitingSent", { count: sent }), data: { count: sent } });
    if (refused.length) {
      notify({ code: NOTICE.WAITING_FILES_REFUSED, level: "warning", duration: 10000,
        message: t("vault.device.waitingRefused", { count: refused.length, reason: refused[0].reason }),
        data: { count: refused.length, reason: refused[0].reason } });
    }
  } catch (error) {
    console.warn("[OfflineFiles] Sending files stopped:", error?.message || error);
  }
}

/**
 * @param {{getCurrentUser: Function, getDocId: () => string|null, getTree: Function,
 *   getDocNotes: Function, getRoutineEvents: Function}} options
 */
export function initOfflineFiles(options) {
  deps = options;
  void loadWaitingFiles();
  // The open map's own sync completed (shared maps' channels carry a shareId).
  on(Events.SYNC_STATE_CHANGED, ({ state, shareId }) => {
    if (!shareId && state === "synced") void copyOpenMap();
  });
  window.addEventListener("online", () => { void sendWaiting(); });
  const reachable = (level) => level === ServerLevel.OK || level === ServerLevel.BUSY;
  let wasReachable = false;
  on(Events.SERVER_STATUS_CHANGED, (status) => {
    if ((reachable(status.level) && !wasReachable) || status.text === "app.status.sendingFiles") {
      void sendWaiting();
    }
    wasReachable = reachable(status.level);
  });
}
